import React from 'react'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  FormikLabeledCheckBox,
  FormikSelectInput,
  FormikTextInput,
} from 'components/molecules/formik-field'
import LoadingButton from 'components/atoms/loading-button'
import { MarktplaatsDetailSchema } from 'config/validation-schemas'
import Icon from 'components/atoms/icon'
import { formatDateTillNowInDays } from 'utilities/format'
import Typography from 'components/molecules/typography'
import {
  CarFileMarktplaatsStatusEnum,
  AdvertisementPortalIdEnum,
} from 'config/enums'
import { useDispatch } from 'react-redux'
import { publishPortal, getCarfile } from 'redux/actions/data'
import Button from 'components/atoms/button'
import toast from 'utilities/toast'

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizings.lvl2};
  padding: ${({ theme }) => theme.sizings.lvl2} 0;
  margin-top: ${({ theme }) => theme.sizeByFactor(4)};
  width: ${({ theme }) => theme.sizeByFactor(30)};
`

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizings.lvl2};
`

const StyledButton = styled(LoadingButton)`
  align-self: flex-start;
`

const StyledMarktplaatsStatistics = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizeByFactor(4)};
`

const StyledLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizings.lvl2};
`

const StyledInsight = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.brandAlfa};
`

const StyledErrorMessage = styled.div`
  margin-bottom: ${({ theme }) => theme.sizings.lvl2};

  a {
    color: ${({ theme }) => theme.colors.brandAlfa};
    text-decoration: underline;
  }
`

function MarktplaatsDetailActions({
  initialData,
  onSubmit,
  marktplaatsInsights,
  carId,
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isPublishing, setIsPublishing] = React.useState(false)
  const [publishStatus, setPublishStatus] = React.useState(null)

  const initialValues = {
    id: initialData?.id || '',
    prijstype: initialData?.prijstype || '0',
    minimaal_bod: initialData?.minimaal_bod || '',
    omhoogplaatsen: initialData?.omhoogplaatsen || false,
    has_dagtopper: initialData?.has_dagtopper || false,
    has_dagtopper7: initialData?.has_dagtopper7 || false,
    has_blikvanger: initialData?.has_blikvanger || false,
    has_opvalsticker: initialData?.has_opvalsticker || false,
    has_fotoknaller: initialData?.has_fotoknaller || false,
    herplaatsen: initialData?.herplaatsen || false,
    herplaatsen_aantal: initialData?.herplaatsen_aantal || 0,
    opvaltekst: initialData?.opvaltekst || '',
  }

  const handleSubmit = async (values) => {
    const submitData = {
      id: values.id,
      prijstype: values.prijstype,
      minimaal_bod: values.minimaal_bod,
      omhoogplaatsen: values.omhoogplaatsen,
      has_dagtopper: values.has_dagtopper,
      has_dagtopper7: values.has_dagtopper7,
      has_blikvanger: values.has_blikvanger,
      has_opvalsticker: values.has_opvalsticker,
      has_fotoknaller: values.has_fotoknaller,
      herplaatsen: values.herplaatsen,
      herplaatsen_aantal: values.herplaatsen_aantal,
      opvaltekst: values.opvaltekst,
    }
    await onSubmit(submitData)
  }

  const handlePublishCar = async () => {
    if (!carId) {
      return
    }
    setIsPublishing(true)
    try {
      await dispatch(
        publishPortal(
          'portalPublish',
          {
            auto_id: Number(carId),
            portal_ids: [Number(AdvertisementPortalIdEnum.MARKTPLAATS)],
          },
          () => {
            setPublishStatus('pending')
            toast.success(t('carFilePublishedOnline'))
            dispatch(getCarfile(carId))
          },
        ),
      )
    } finally {
      setIsPublishing(false)
    }
  }

  if (
    publishStatus === 'pending' ||
    marktplaatsInsights?.status === CarFileMarktplaatsStatusEnum.PENDING
  ) {
    return (
      <div>
        <Typography type="BodyParagraph">
          {t('marktplaatsView.status.pending')}
        </Typography>
      </div>
    )
  }

  if (marktplaatsInsights?.status === CarFileMarktplaatsStatusEnum.ERROR) {
    return (
      <div>
        <Typography type="BodyParagraph">
          {t('marktplaatsView.status.error')}
        </Typography>
        {marktplaatsInsights.statusLog && (
          <Typography type="BodyParagraph">
            <StyledErrorMessage
              dangerouslySetInnerHTML={{ __html: marktplaatsInsights.statusLog }}
            />
          </Typography>
        )}
        <Button
          level="option"
          text={t('publish')}
          noPadding
          size="24"
          onClick={handlePublishCar}
          isLoading={isPublishing}
        />
      </div>
    )
  }

  if (
    !marktplaatsInsights ||
    marktplaatsInsights.status !== CarFileMarktplaatsStatusEnum.PUBLISHED
  ) {
    return (
      <div>
        <Typography type="BodyParagraph">
          {t('marktplaatsView.notYetPublished')}
        </Typography>
        <Button
          level="option"
          text={t('publish')}
          noPadding
          size="24"
          onClick={handlePublishCar}
          isLoading={isPublishing}
        />
      </div>
    )
  }

  return (
    <>
      <Typography type="Level2Heading">
        {t('marktplaatsView.statistics.title')}
      </Typography>
      <StyledMarktplaatsStatistics>
        {marktplaatsInsights?.expires_at && (
          <div>
            <StyledLabel>
              <Icon size="md" type="accessTime" />
              <Typography type="BodyParagraph">
                {t('marktplaatsView.statistics.expiresAt')}
              </Typography>
            </StyledLabel>
            <StyledInsight>
              {formatDateTillNowInDays(marktplaatsInsights?.expires_at)}{' '}
              {t('dys').toLocaleLowerCase()}
            </StyledInsight>
          </div>
        )}
        <div>
          <StyledLabel>
            <Icon size="md" type="search" />
            <Typography type="BodyParagraph">
              {t('marktplaatsView.statistics.clicks')}
            </Typography>
          </StyledLabel>
          <StyledInsight>{marktplaatsInsights?.clicks}</StyledInsight>
        </div>
        <div>
          <StyledLabel>
            <Icon size="md" type="views" />
            <Typography type="BodyParagraph">
              {t('marktplaatsView.statistics.views')}
            </Typography>
          </StyledLabel>
          <StyledInsight>{marktplaatsInsights?.views}</StyledInsight>
        </div>
      </StyledMarktplaatsStatistics>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={MarktplaatsDetailSchema(t)}
      >
        {({ values, isSubmitting, dirty }) => (
          <StyledForm>
            <Typography type="Level2Heading">
              {t('marktplaatsForm.formLabels.priceType')}
            </Typography>
            <div>
              <Field
                name="prijstype"
                label={t('marktplaatsForm.formLabels.priceType')}
                items={[
                  {
                    label: t('marktplaatsForm.priceTypeValues.askingPrice'),
                    value: '0',
                  },
                  {
                    label: t('marktplaatsForm.priceTypeValues.reserved'),
                    value: '1',
                  },
                  {
                    label: t('marktplaatsForm.priceTypeValues.priceOffer'),
                    value: '2',
                  },
                  {
                    label: t('marktplaatsForm.priceTypeValues.seeDescription'),
                    value: '6',
                  },
                  {
                    label: t('marktplaatsForm.priceTypeValues.priceOfferFrom'),
                    value: '8',
                  },
                ]}
                component={FormikSelectInput}
                filled
              />
            </div>
            {values.prijstype === '8' && (
              <div>
                <Field
                  name="minimaal_bod"
                  label={t('marktplaatsForm.formLabels.offerFrom')}
                  component={FormikTextInput}
                  type="number"
                  unit="currency_euro"
                  filled
                />
              </div>
            )}
            <Typography type="Level2Heading">
              {t('actions')}
            </Typography>
            <CheckboxGroup>
              <Field
                name="omhoogplaatsen"
                label={t('marktplaatsForm.formLabels.raisePosition')}
                component={FormikLabeledCheckBox}
              />
              <Field
                name="has_dagtopper"
                label={t('marktplaatsForm.formLabels.dayTopper')}
                component={FormikLabeledCheckBox}
              />
              <Field
                name="has_dagtopper7"
                label={t('marktplaatsForm.formLabels.sevenDaysDayTopper')}
                component={FormikLabeledCheckBox}
              />
              <Field
                name="has_blikvanger"
                label={t('marktplaatsForm.formLabels.eyeCatcher')}
                component={FormikLabeledCheckBox}
              />
              <Field
                name="has_opvalsticker"
                label={t('marktplaatsForm.formLabels.attentionSticker')}
                component={FormikLabeledCheckBox}
              />
              {values.has_opvalsticker && (
                <Field
                  name="opvaltekst"
                  label={t('marktplaatsForm.formLabels.attentionText')}
                  component={FormikTextInput}
                  filled
                />
              )}
              <Field
                name="herplaatsen"
                label={t('marktplaatsForm.formLabels.rePost')}
                component={FormikLabeledCheckBox}
              />
              {values.herplaatsen && (
                <Field
                  name="herplaatsen_aantal"
                  label={t('marktplaatsForm.formLabels.noOfTimes')}
                  items={[
                    {
                      label: t('marktplaatsForm.noOfTimesValues.everyMonth'),
                      value: 0,
                    },
                    {
                      label: t(
                        'marktplaatsForm.noOfTimesValues.anotherSingleMonth',
                      ),
                      value: 1,
                    },
                    {
                      label: t(
                        'marktplaatsForm.noOfTimesValues.another2Months',
                      ),
                      value: 2,
                    },
                    {
                      label: t(
                        'marktplaatsForm.noOfTimesValues.another3Months',
                      ),
                      value: 3,
                    },
                    {
                      label: t(
                        'marktplaatsForm.noOfTimesValues.another4Months',
                      ),
                      value: 4,
                    },
                  ]}
                  component={FormikSelectInput}
                  filled
                />
              )}
            </CheckboxGroup>

            <StyledButton
              text={t('save')}
              level="cta"
              type="submit"
              isLoading={isSubmitting}
              disabled={!dirty}
              data-test-e2e="button-submit-marktplaats-detail"
            />
          </StyledForm>
        )}
      </Formik>
    </>
  )
}

MarktplaatsDetailActions.propTypes = {
  initialData: PropTypes.shape({
    id: PropTypes.string,
    prijstype: PropTypes.string,
    minimaal_bod: PropTypes.string,
    omhoogplaatsen: PropTypes.bool,
    has_dagtopper: PropTypes.bool,
    has_dagtopper7: PropTypes.bool,
    has_blikvanger: PropTypes.bool,
    has_opvalsticker: PropTypes.bool,
    has_fotoknaller: PropTypes.bool,
    herplaatsen: PropTypes.bool,
    herplaatsen_aantal: PropTypes.number,
    opvaltekst: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
  marktplaatsInsights: PropTypes.object,
  carId: PropTypes.string.isRequired,
}

MarktplaatsDetailActions.defaultProps = {
  initialData: null,
  marktplaatsInsights: null,
}

export default MarktplaatsDetailActions
